import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  TablePagination,
  Grid,
  CardHeader,
  CardContent,
  Paper,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';

import { Page } from '../../../components';
import { fDate } from '../../../utils/formatTime';
import { getAssetAuditRecords } from '../store/auditActions';

const TABLE_HEAD = [
  { id: 'name', label: 'Audit', alignRight: false },
  { id: 'location', label: 'Location', alignRight: false },
  { id: 'auditor', label: 'Auditor', alignRight: false },
  { id: 'audit_date', label: 'Date', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
];

AssetAudit.propTypes = {
  assetId: PropTypes.string,
};

export default function AssetAudit({ assetId }) {
  const dispatch = useDispatch();
  // The store used to keep this list under `scannedAssets`, shared with the
  // reconcile screen, so whichever page loaded last overwrote the other.
  const { isLoading, assetAudits, assetAuditCount } = useSelector((state) => state.auditReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    dispatch(getAssetAuditRecords({ id: assetId, page: page + 1, rowsPerPage }));
  }, [dispatch, assetId, page, rowsPerPage]);

  return (
    <Page title="Audit History">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CardHeader title="Audit History" subheader="Stock takes this asset was counted in" />
            <CardContent>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 800 }} component={Paper}>
                  {isLoading ? (
                    <Grid container sx={{ justifyContent: 'center' }}>
                      <ThreeDots height="80" width="80" radius="9" color="#2065d1" ariaLabel="three-dots-loading" visible />
                    </Grid>
                  ) : (
                    <Table size="small">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={assetAudits?.length} />
                      <TableBody>
                        {assetAudits?.length > 0 ? (
                          assetAudits.map((record) => (
                            <TableRow hover key={record?.id} tabIndex={-1}>
                              <TableCell align="left">
                                <RouterLink to={`/dashboard/audits/${record?.id}`}>{record?.name}</RouterLink>
                              </TableCell>
                              <TableCell align="left">{record?.location?.name}</TableCell>
                              <TableCell align="left">{record?.auditor?.user?.display_name}</TableCell>
                              <TableCell align="left">{fDate(record?.audit_date)}</TableCell>
                              <TableCell align="left">
                                <Label color={record?.audit_completed ? 'success' : 'info'}>
                                  {(record?.status || '').replace(/_/g, ' ').toLowerCase()}
                                </Label>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          // A bare string here is not a legal child of <tbody>;
                          // it used to render as a stray text node and warn.
                          <TableRow>
                            <TableCell align="center" colSpan={TABLE_HEAD.length} sx={{ py: 3 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                This asset has not been counted in any stock take yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Scrollbar>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={assetAuditCount || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
